import type { ImageRaster } from '@/modules/Editor/models/raster/ImageRaster'
import type { FrameData } from '@/core/annotations'
import type { ConsensusRasterRemap } from '@/modules/Editor/models/raster/consensusRasterRemap'
import { decodeDenseRLE } from '@/modules/Editor/plugins/mask/rle/denseRle'

export class RasterTooLargeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RasterTooLargeError'
  }
}

/**
 * Deserializes the raster annotations and apply the buffer to the raster
 *
 * When `consensusRemap` is provided, the raster is treated as a non-default
 * annotation group overlay: each id in `mask_annotation_ids_mapping` is
 * rewritten via `sourceIdToDefaultId`. Mask labels with no remap entry are
 * left in the pixel buffer but unmapped, so they render transparent.
 */
export const deserializeImageRaster = (
  imageRaster: ImageRaster,
  rasterLayerDataPayload: FrameData['raster_layer'],
  annotationIdToClassIdMapping: Record<string, number>,
  consensusRemap?: ConsensusRasterRemap,
): void => {
  if (rasterLayerDataPayload === undefined) {
    throw new Error('No raster_layer on raster annotation')
  }

  const maskAnnotationIdsMapping = rasterLayerDataPayload.mask_annotation_ids_mapping
  const denseRLE = rasterLayerDataPayload.dense_rle
  const totalPixels = rasterLayerDataPayload.total_pixels

  if (!denseRLE || denseRLE[0] === undefined) {
    return
  }

  const annotationIds = Object.keys(maskAnnotationIdsMapping)

  const { mask, boundsPerLabelIndex, totalBounds } = decodeDenseRLE(
    denseRLE,
    totalPixels,
    imageRaster.width,
  )

  annotationIds.forEach((sourceAnnotationId) => {
    const labelIndex = maskAnnotationIdsMapping[sourceAnnotationId]
    if (!labelIndex) {
      return
    }

    let annotationId = sourceAnnotationId

    if (consensusRemap) {
      const remappedId = consensusRemap.sourceIdToDefaultId.get(sourceAnnotationId)
      if (!remappedId) {
        // The mask is not in disagreement; leave its pixels unmapped so they
        // render transparent.
        return
      }
      annotationId = remappedId
    }

    const classId = annotationIdToClassIdMapping[annotationId]

    if (classId === undefined) {
      // We would normally throw an error here, but we can't do that as have sometimes leftovers
      // from the old raster layer that are not in the annotationIdToClassIdMapping.
      return
    }
    imageRaster.setAnnotationMapping(labelIndex, annotationId, classId)

    /**
     * It is completely valid for a mask class to exist (e.g. through API),
     * without it having a single voxel on the raster layer. If it does not,
     * we set its range to zero.
     */
    const bounds = boundsPerLabelIndex[labelIndex] || {
      topLeft: { x: 0, y: 0 },
      bottomRight: { x: 0, y: 0 },
    }

    imageRaster.setBoundsForLabelIndex(labelIndex, bounds)
  })

  imageRaster.setActiveBuffer(mask)

  // Invalidate only the region containing data.
  imageRaster.invalidate(
    totalBounds.topLeft.x,
    totalBounds.bottomRight.x,
    totalBounds.topLeft.y,
    totalBounds.bottomRight.y,
  )
}
